import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { AccountType, AccountUpdateParameters, CompanyOptions } from '@mews-commander/data-access';
import { AsyncAction, CommanderThunkAction } from '@mews-commander/types';
import { DurationService, lt, showSuccessAlert } from '@mews/framework';
import { createFetchAction, createUpdateValue } from '@mews-commander/utils';
import { updateAccount } from '@mews-commander/legacy-entities/src/actions';
import { COMPANY_DETAIL_CONTACT_INFO_UPDATE } from '../actionTypes';
import { getAdditionalInfoFormInitialValues } from '../selectors';
import { AdditionalInfoFormValues } from '../types';
import { isEqualWithUndefined } from '../utils';

const getStringDaysISODuration = (days: number) => {
    const duration = DurationService.fromObject({ days });
    return DurationService.toString(duration);
};

export const updateAdditionalInfo =
    (
        {
            contactPerson,
            classifications,
            contact,
            sourceId,
            iata,
            invoiceDueInterval,
            options,
        }: AdditionalInfoFormValues,
        companyId: string
    ): CommanderThunkAction<AsyncAction, void | Promise<unknown>> =>
    async (dispatch, getState) => {
        const initialValues = getAdditionalInfoFormInitialValues(getState(), { id: companyId });
        if (!initialValues) {
            return;
        }

        let stringDaysISODuration = null;
        if (invoiceDueInterval) {
            stringDaysISODuration = getStringDaysISODuration(Number(invoiceDueInterval));
        }

        let initialDaysString: string | null = null;
        if (initialValues?.invoiceDueInterval) {
            initialDaysString = getStringDaysISODuration(initialValues.invoiceDueInterval);
        }

        const optionsPayload = pipe(
            options,
            array.reduce<string, Record<string, boolean>>({}, (acc, cur) => {
                if (cur === CompanyOptions.Invoiceable) {
                    return { ...acc, isInvoiceable: true };
                }
                return { ...acc, [cur]: true };
            })
        );

        const classificationsPayload = pipe(
            classifications,
            array.reduce<string, Record<string, boolean>>({}, (acc, cur) => ({ ...acc, [cur]: true }))
        );

        const accountUpdates: AccountUpdateParameters[] = [
            {
                data: {
                    discriminator: AccountType.Company,
                    value: {
                        classifications: isEqualWithUndefined(initialValues.classifications, classifications)
                            ? undefined
                            : { value: classificationsPayload },
                        contact: createUpdateValue(initialValues.contact, contact),
                        contactPerson: createUpdateValue(initialValues.contactPerson, contactPerson),
                        iata: createUpdateValue(initialValues.iata, iata),
                        id: companyId,
                        invoiceDueInterval: createUpdateValue(initialDaysString, stringDaysISODuration),
                        options: isEqualWithUndefined(initialValues.options, options)
                            ? undefined
                            : { value: optionsPayload },
                        sourceId: createUpdateValue(initialValues.sourceId, sourceId),
                    },
                },
            },
        ];
        const promise = dispatch(updateAccount({ accountUpdates })).then(() =>
            showSuccessAlert({
                description: lt('SaveSuccessMessage'),
            })
        );
        return dispatch(createFetchAction(COMPANY_DETAIL_CONTACT_INFO_UPDATE, promise));
    };
